import { useCallback, useMemo, useState } from 'react'
import { object, string, number, SchemaOf, array } from 'yup'
import moment from 'moment'

import { Formik, Form } from 'formik'

import { Alert, Fade } from '@mui/material'

import { mimeTypes } from '@collaborate-health/uikit/constants'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DragAndDropFiles,
  Stack,
} from '@collaborate-health/uikit'

import { FormDatePicker, FormTextField } from '@collaborate-health/forms'
import {
  PeriodUnitType,
  type CreateCertificateRequest,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { maxLength, noWhitespaces, requiredField } from '@tabeeb/shared/utils/validationErrorMessages'
import { readUploadedFileAsDataUrl } from '@tabeeb/modules/fileUploads/services'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { useCertificate, useCertificateLocations, useAllCertificateTypes } from '../../hooks'
import { Certificate } from '../../types'

import { CertificateTypeField, LocationsField } from '../Fields'
import AddEditCertificateFormSkeleton from './AddEditCertificateFormSkeleton'
import CertificatePreview from '../CertificatePreview'

export interface AddEditCertificateDialogProps {
  open: boolean
  onClose: () => void
  certificate?: Certificate | null
  onSubmit: (params: { values: CreateCertificateRequest; editMode: boolean }) => Promise<void>
  onExited: () => void
}

const schema: SchemaOf<CreateCertificateRequest> = object().shape({
  SerialNumber: string().strict().nullable().trim(noWhitespaces).max(256, maxLength(256)),
  Authority: string().strict().trim(noWhitespaces).max(256, maxLength(256)),
  ValidFrom: string()
    .nullable()
    .required(requiredField)
    .test('is-valid-date', 'Valid from must be a valid date', (value) => moment(value).isValid())
    .test('min-date', 'Valid from must be after January 1, 1900', (value) =>
      moment(value).isAfter(moment('1900-01-01'))
    )
    .test('max-date', 'Valid from cannot be in the future', (value) => moment(value).isSameOrBefore(moment(), 'day')),
  ExpirationDate: string()
    .nullable()
    .required(requiredField)
    .test('is-valid-date', 'Valid till must be a valid date', (value) => moment(value).isValid())
    .test('after-valid-from', 'Valid till must be after valid from date', function validateAfterValidFrom(value) {
      const { ValidFrom } = this.parent
      return !ValidFrom || moment(value).isAfter(moment(ValidFrom), 'day')
    }),
  TypeId: number().nullable().strict().required(requiredField),
  PhotoData: string().required(),
  LocationIds: array().of(number().required()).required(),
})

const calculateValidToDate = (
  validFrom: Date | string,
  defaultValidityPeriod: number,
  defaultValidityPeriodUnitId: PeriodUnitType
): Date | null => {
  if (!validFrom) {
    return null
  }

  const result = new Date(validFrom)
  switch (defaultValidityPeriodUnitId) {
    case PeriodUnitType.Day:
      result.setDate(result.getDate() + defaultValidityPeriod)
      break
    case PeriodUnitType.Month:
      result.setMonth(result.getMonth() + defaultValidityPeriod)
      break
    case PeriodUnitType.Year:
      result.setFullYear(result.getFullYear() + defaultValidityPeriod)
      break
    default:
      break
  }

  return result
}

const AddEditCertificateDialog: React.FC<AddEditCertificateDialogProps> = ({
  open,
  certificate: certificateBase,
  onClose,
  onSubmit,
  onExited,
}) => {
  const dispatch = useDispatch()

  const editMode = Boolean(certificateBase)

  const onError = useCallback(() => {
    dispatch(onAddErrorNotification({ message: 'Failed to load certificate details' }))
    onClose()
  }, [dispatch, onClose])

  const { certificate: selectedCertificate, loading: certificateLoading } = useCertificate({
    id: certificateBase?.Id!,
    enabled: open && editMode,
    subdomain: certificateBase?.TenantSubdomain,
    onError,
  })

  const { loading: typesLoading, certificateTypes: types } = useAllCertificateTypes({ enabled: open })
  const { loading: locationsLoading, locations } = useCertificateLocations({ enabled: open })

  const [certificateFile, setCertificateFile] = useState<File | null>(null)

  const editCertificateLoading = useMemo(
    () => Boolean(certificateBase) && (certificateLoading || !selectedCertificate?.Id),
    [certificateLoading, selectedCertificate?.Id, certificateBase]
  )

  const initialValues = useMemo<CreateCertificateRequest>(
    () => ({
      SerialNumber: selectedCertificate?.SerialNumber ?? '',
      Authority: selectedCertificate?.Authority ?? '',
      ValidFrom: selectedCertificate?.ValidFrom ?? '',
      CategoryId: selectedCertificate?.CategoryId ?? null!,
      ScopeId: selectedCertificate?.ScopeId ?? null!,
      TypeId: selectedCertificate?.TypeId ?? null!,
      ExpirationDate: selectedCertificate?.ExpirationDate ?? '',
      PhotoData: selectedCertificate?.PictureUrl ?? null!,
      LocationIds: selectedCertificate?.Locations?.map((l) => l.Id) ?? [],
    }),
    [selectedCertificate]
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          setCertificateFile(null)
          onExited()
        },
      }}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle>{editMode ? 'Edit certificate' : 'Create certificate'}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          await onSubmit({
            values,
            editMode,
          })
        }}
        validationSchema={schema}
      >
        {({ values, isSubmitting, isValid, dirty, setFieldValue, setFieldTouched }) => {
          const type = types.find((t) => t.Id === values.TypeId)

          return (
            <Form style={{ display: 'contents' }} autoComplete='off'>
              <DialogContent>
                {editCertificateLoading ? (
                  <AddEditCertificateFormSkeleton />
                ) : (
                  <Stack spacing={2}>
                    {values.PhotoData && (
                      <Stack width={200}>
                        <CertificatePreview src={values.PhotoData} />
                      </Stack>
                    )}
                    <Fade in={Boolean(selectedCertificate?.RejectionReason)} unmountOnExit>
                      <Alert severity='error' title='Rejection reason' variant='outlined'>
                        {selectedCertificate?.RejectionReason}
                      </Alert>
                    </Fade>
                    <FormTextField disabled={isSubmitting} name='SerialNumber' label='Serial number' />
                    <FormTextField disabled={isSubmitting} name='Authority' label='Authority' />
                    <CertificateTypeField
                      name='TypeId'
                      disabled={isSubmitting || editMode}
                      loading={typesLoading}
                      types={types}
                      scope={type?.ScopeId}
                      category={type?.CategoryId}
                      onChange={(_, newTypeId) => {
                        const newType = types.find((t) => t.Id === newTypeId)
                        if (!newType) {
                          return
                        }

                        if (values.ValidFrom && newType.DefaultValidityPeriodUnitId && newType.DefaultValidityPeriod) {
                          const validTo = calculateValidToDate(
                            values.ValidFrom,
                            newType.DefaultValidityPeriod,
                            newType.DefaultValidityPeriodUnitId
                          )

                          if (validTo) {
                            setFieldValue('ExpirationDate', validTo)
                            setFieldTouched('ExpirationDate')
                          }
                        }
                      }}
                    />
                    <Stack direction='row' spacing={2}>
                      <FormDatePicker
                        fullWidth
                        format='MM/DD/YYYY'
                        name='ValidFrom'
                        label='Valid from'
                        title='The required date format is MM/DD/YYYY'
                        disabled={isSubmitting}
                        onChange={(date) => {
                          if (date && type && type.DefaultValidityPeriodUnitId && type.DefaultValidityPeriod) {
                            const validTo = calculateValidToDate(
                              date,
                              type.DefaultValidityPeriod,
                              type.DefaultValidityPeriodUnitId
                            )

                            if (validTo) {
                              setFieldValue('ExpirationDate', validTo)
                              setFieldTouched('ExpirationDate')
                            }
                          }
                        }}
                      />
                      <FormDatePicker
                        fullWidth
                        format='MM/DD/YYYY'
                        name='ExpirationDate'
                        label='Valid till'
                        title='The required date format is MM/DD/YYYY'
                        disabled={isSubmitting}
                      />
                    </Stack>

                    <LocationsField
                      name='LocationIds'
                      disabled={isSubmitting}
                      locations={locations}
                      loading={locationsLoading}
                    />

                    <DragAndDropFiles
                      file={certificateFile}
                      disabled={isSubmitting}
                      onDropAccepted={async (files) => {
                        const file = files[0]
                        if (!file) {
                          return
                        }

                        setFieldValue('PhotoData', await readUploadedFileAsDataUrl(file))
                        setCertificateFile(file)
                      }}
                      onClearState={() => {
                        setFieldValue('PhotoData', null)
                        setCertificateFile(null)
                      }}
                      placeholder='Please select a certificate photo to upload'
                      fileUploadButtonText='Upload photo'
                      acceptMimeFormats={{
                        [mimeTypes.jpeg]: ['.jpeg', '.jpg'],
                        [mimeTypes.png]: [],
                        [mimeTypes.bmp]: [],
                        [mimeTypes.pdf]: ['.pdf'],
                      }}
                      acceptedFormatDisplayStrings={['png', 'jpg', 'jpeg', 'bmp', 'pdf']}
                    />
                  </Stack>
                )}
              </DialogContent>
              <DialogActions>
                <Button color='primary' onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                  color='primary'
                  variant='contained'
                  type='submit'
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default AddEditCertificateDialog

